import { FC, useCallback, useEffect, useState } from 'react';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';

import { skipToken } from '@reduxjs/toolkit/query';

import { clearInventering } from 'store/aktivitetSlice';
import { clearBebyggelseobjektUtkast, getBebyggelseobjektUtkast } from 'store/bebyggelseobjektUtkastSlice';
import { setMapMode } from 'store/mapSlice';
import { clearPubliceradeBebyggelseobjekt } from 'store/publiceradeBebyggelseobjektSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';
import { useDrawerMobileTemplate } from 'hooks/useDrawerMobileTemplate';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';

import { RaaExpandablePreview } from 'raa-react';

import { CenterActiveSourceGeometryButton } from 'components/CenterActiveSourceGeometryButton/CenterActiveSourceGeometryButton';
import InventeringStatusSection from 'components/aktivitetPresentation/inventering/inventeringStatusSection/InventeringStatusSection';
import PubliceraInventeringDialog from 'components/aktivitetPresentation/inventering/publiceraInventeringDialog/PubliceraInventeringDialog';
import PubliceradeBebyggelseobjekt from 'components/aktivitetPresentation/publiceradeBebyggelseobjekt/PubliceradeBebyggelseobjekt';
import {
  DrawerBody,
  DrawerContainer,
  DrawerFooter,
  DrawerSection,
  NavigationDrawerHead
} from 'components/drawerSections/DrawerSections';
import GeometryFileDownload from 'components/geometryFileDownload/GeometryFileDownload';
import { activeSource, replaceInventeringInActiveSource } from 'components/map/Layers';
import { Colors } from 'components/map/Styles';
import { inventeringCircle } from 'components/map/icons';

import { AktivitetStatusTyp, GeografiskIndelning, HTTP_STATUS } from 'models';

import { AktivitetRegistreringsstatustypEnum, AktivitetStatusEnum, MapModeEnum } from 'enums';

import { useGetAktivitetPreliminarQuery } from 'api/endpoints/aktivitetPreliminarSlice';
import { isAnsvarigOrganisation, isAnsvarigOrganisationOrGranskare } from 'utilities/bebyggelseobjektUtilities';
import { renderWithParagraphs } from 'utilities/renderWithParagraphs';

//TODO borde kanske mergas samman med InventeringPresentation ?
const InventeringPreliminarPresentation: FC = () => {
  const { id } = useParams<string>();
  const dispatch = useAppDispatch();

  const { data: aktivitetDomaner, requestStatus: aktivitetDomanerStatus } = useAppSelector(
    (state: RootState) => state.aktivitetDomaner
  );
  const { selected_organisation, rolesInSelectedOrganisation } = useAppSelector((state: RootState) => state.user);
  const { setDialog } = useDialog();
  const domanerIsLoading: boolean = aktivitetDomanerStatus === 'pending';
  const [patchIsLoading, setPatchIsLoading] = useState<boolean>(false);

  const [geometryFileName, setGeometryFileName] = useState<string>();
  const navigate: NavigateFunction = useNavigate();

  const { setDrawerMobileTemplate } = useDrawerMobileTemplate();

  const { data: inventeringPreliminarData, error: inventeringPreliminarError } = useGetAktivitetPreliminarQuery(
    id ?? skipToken,
    {
      refetchOnMountOrArgChange: true,
      skip: patchIsLoading
    }
  );

  useEffect(() => {
    //Cleanup
    return () => {
      setDrawerMobileTemplate(undefined);
      dispatch(clearInventering());
      dispatch(clearBebyggelseobjektUtkast());
      dispatch(clearPubliceradeBebyggelseobjekt());
      activeSource.clear();
      activeSource.changed();
      dispatch(setMapMode(MapModeEnum.DEFAULT));
    };
  }, [dispatch, setDrawerMobileTemplate]);

  useEffect(() => {
    if (
      inventeringPreliminarError &&
      'status' in inventeringPreliminarError &&
      inventeringPreliminarError.status === HTTP_STATUS.NOT_FOUND
    ) {
      navigate(`/aktivitet/${id}`, { replace: true });
    } else {
      dispatch(clearBebyggelseobjektUtkast());
      dispatch(clearPubliceradeBebyggelseobjekt());
      dispatch(setMapMode(MapModeEnum.INVENTERING_PRESENTATION));
    }
  }, [
    dispatch,
    navigate,
    id,
    inventeringPreliminarError,
    inventeringPreliminarData?.aktivitet_registrering_status.registreringsstatustyp_id
  ]);

  useInitialFocusOnExtent(
    useCallback(
      () =>
        inventeringPreliminarData?.inventeringsomrade &&
        replaceInventeringInActiveSource({
          featureCollection: inventeringPreliminarData.inventeringsomrade,
          inventering: inventeringPreliminarData
        }),
      [inventeringPreliminarData]
    )
  );

  useEffect(() => {
    if (inventeringPreliminarData) {
      setGeometryFileName(
        `${inventeringPreliminarData.aktivitetsnummer} ${inventeringPreliminarData.inventeringsomrade_datum}`
      );

      if (
        selected_organisation &&
        isAnsvarigOrganisationOrGranskare(inventeringPreliminarData, selected_organisation, rolesInSelectedOrganisation)
      ) {
        dispatch(getBebyggelseobjektUtkast(inventeringPreliminarData.aktivitet_id));
      }
    }
  }, [inventeringPreliminarData, selected_organisation, rolesInSelectedOrganisation, dispatch]);

  useEffect(() => {
    setDrawerMobileTemplate(
      <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
        <h1 className="tw-heading-md tw-w-64 tw-truncate md:tw-w-full">{inventeringPreliminarData?.titel || ''}</h1>
        <div className="tw-flex tw-items-center tw-justify-start">
          <img
            className="tw-mr-2"
            src={'data:image/svg+xml,' + encodeURIComponent(inventeringCircle(Colors.RAA_BLUE, Colors.WHITE))}
            alt="Ikon för inventering"
          />
          <p> Inventering {inventeringPreliminarData ? inventeringPreliminarData.aktivitetsnummer : '(ej sparad)'}</p>
        </div>
      </div>
    );
  }, [inventeringPreliminarData, setDrawerMobileTemplate]);

  const getKommunAndLanText = (geografiskIndelning: GeografiskIndelning): string =>
    geografiskIndelning.kommun.map((kommun) => `${kommun.kommun_namn} kommun, ${kommun.lan_namn} län`).join(', ');

  if (!inventeringPreliminarData) return <></>;

  return (
    <DrawerContainer>
      <NavigationDrawerHead disableBackNavigation={true} />
      <DrawerBody>
        <DrawerSection>
          <InventeringStatusSection inventeringPreliminar={inventeringPreliminarData} />
          <h1 className="tw-heading-lg tw-mb-6">{inventeringPreliminarData.titel}</h1>
          <div className="tw-my-4 tw-flex tw-items-center tw-justify-between tw-text-center">
            <div className="tw-flex tw-items-center">
              <img
                src={'data:image/svg+xml,' + encodeURIComponent(inventeringCircle(Colors.RAA_BLUE, Colors.WHITE))}
                alt="Ikon för inventering"
              />
              <p className="tw-ml-2">Inventering {inventeringPreliminarData.aktivitetsnummer}</p>
            </div>
            <p className="raa-secondary-text tw-block tw-max-w-[50%] tw-items-center tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
              {getKommunAndLanText(inventeringPreliminarData.geografisk_indelning)}
            </p>
          </div>

          <p className="tw-mb-4">
            {
              aktivitetDomaner?.aktivitettyper.find(
                (aktivitettyp) => aktivitettyp.aktivitettyp === inventeringPreliminarData?.aktivitetstyp
              )?.beskrivning
            }
          </p>

          <table className="tw-mb-6 tw-w-full">
            <tbody className="tw-w-full">
              <tr>
                <th className="tw-text-left">Status</th>
                <td>
                  {aktivitetDomaner?.aktivitetstatustyper.find(
                    (typ: AktivitetStatusTyp) =>
                      typ.aktivitetstatustyp === inventeringPreliminarData.aktivitetstatus.status
                  )?.namn || 'HITTADE INGEN STATUS'}
                </td>
              </tr>
              <tr>
                <th className="tw-text-left">Ansvarig organisation</th>
                <td>{inventeringPreliminarData.ansvarig_organisation.organisationsnamn}</td>
              </tr>
              <tr>
                <th className="tw-text-left">Inventeringsår</th>
                <td>
                  {inventeringPreliminarData.start_ar}
                  {inventeringPreliminarData.slut_ar && `–${inventeringPreliminarData.slut_ar}`}
                </td>
              </tr>
              <tr>
                <th className="tw-text-left">Inventeringsyta</th>
                <td className="tw-flex tw-gap-2">
                  <CenterActiveSourceGeometryButton />
                  {inventeringPreliminarData.inventeringsomrade && geometryFileName && (
                    <GeometryFileDownload
                      fileName={geometryFileName}
                      geometry={inventeringPreliminarData.inventeringsomrade}
                    />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          <h2 className="tw-heading-md tw-mb-4">Syfte och metod</h2>
          <RaaExpandablePreview ariaLabel="syfte">
            <div className="tw-mb-4">{renderWithParagraphs(inventeringPreliminarData.syfte)}</div>
          </RaaExpandablePreview>
          {isAnsvarigOrganisation(inventeringPreliminarData, selected_organisation) &&
            inventeringPreliminarData.aktivitetstatus.status !== AktivitetStatusEnum.AVSLUTAD &&
            inventeringPreliminarData.aktivitet_registrering_status.registreringsstatustyp_id ===
              AktivitetRegistreringsstatustypEnum.REDIGERBAR && (
              <div className="tw-mt-8 tw-flex tw-w-full">
                <button
                  className="raa-button raa-button-secondary"
                  data-testid="editInventering"
                  onClick={() => navigate(`/inventering/${inventeringPreliminarData.aktivitet_id}/redigera`)}
                >
                  Redigera info
                </button>
              </div>
            )}
        </DrawerSection>
        {isAnsvarigOrganisationOrGranskare(
          inventeringPreliminarData,
          selected_organisation,
          rolesInSelectedOrganisation
        ) && (
          <DrawerSection>
            <h2 className="tw-heading-md tw-mb-6">Utkast för inventerade objekt (0)</h2>
            {(!inventeringPreliminarData ||
              inventeringPreliminarData?.aktivitetstatus.status === AktivitetStatusEnum.PLANERAD) && (
              <p>
                {inventeringPreliminarData.aktivitet_registrering_status.registreringsstatustyp_id ===
                AktivitetRegistreringsstatustypEnum.REDIGERBAR
                  ? 'Publicera inventeringen för att kunna lägga till bebyggelseobjekt.'
                  : 'Bebyggelseobjekt kan läggas till när granskningen är klar.'}
              </p>
            )}
          </DrawerSection>
        )}

        <DrawerSection>
          <h2 className="tw-heading-md tw-mb-6">Inventerade objekt (0)</h2>
          <PubliceradeBebyggelseobjekt list={undefined} error={undefined} requestStatus={'idle'} />
        </DrawerSection>
      </DrawerBody>
      {inventeringPreliminarData.aktivitet_registrering_status.registreringsstatustyp_id ===
        AktivitetRegistreringsstatustypEnum.REDIGERBAR && (
        <DrawerFooter>
          <button
            className="raa-button raa-button-cancel tw-flex-1"
            type="button"
            onClick={() => navigate(`/`, { replace: true })}
          >
            Avbryt
          </button>

          <button
            className="raa-button raa-button-confirm tw-w-full md:tw-flex-1"
            type="button"
            disabled={domanerIsLoading}
            onClick={() => {
              setDialog(PubliceraInventeringDialog, {
                id: inventeringPreliminarData.aktivitet_id,
                setPatchIsLoading
              });
            }}
          >
            publicera
          </button>
        </DrawerFooter>
      )}
    </DrawerContainer>
  );
};

export default InventeringPreliminarPresentation;
